import { API_BASE_URL, PRESENTATIONS_API_BASE_URL } from '../config';

// Public API endpoints
const PUBLIC_API = `${API_BASE_URL}/public`;

interface Speaker {
  id: string;
  name: string;
  title: string;
  image_url?: string;
}

interface Session {
  id: string;
  title: string;
  description: string;
  type: string;
  speakers: { id: string; name: string; display_order: number }[];
  startTime: string;
  endTime: string;
  room: string;
  status: string;
}

interface Sponsor {
  id: string;
  name: string;
  logo_url?: string;
  description?: string;
  website?: string;
  tier: string;
}

interface APIResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface SpeakersResponse {
  speakers: Speaker[];
}

interface SessionsResponse {
  sessions: Session[];
}

interface SponsorsResponse {
  sponsors: Sponsor[];
}

export interface PresentationFile {
  filename: string;
  file_size: number;
}

export interface PresentationVersion {
  version_id: string;
  uploaded_at: string;
  total_size?: number;
  files?: PresentationFile[];
  filename?: string;
  file_size?: number;
}

export interface Presentation {
  id: string;
  speaker_name: string;
  speaker_email: string;
  presentation_title: string;
  upload_link: string;
  versions: PresentationVersion[];
  created_at: string;
  updated_at: string;
}

export const getLogo = (small = false) => `${PUBLIC_API}/logo${small ? '?small=true' : ''}`;

// Speaker image upload function
export const uploadSpeakerImage = async (file: File): Promise<{ success: boolean, image_url: string }> => {
  // Create form data
  const formData = new FormData();
  formData.append('file', file);
  
  // Create headers for authenticated request
  const headers: HeadersInit = {};
  
  // Add the authorization token if available
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}/api/speaker/profile/upload-image`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });
  
  if (!response.ok) {
    throw new Error('Failed to upload image');
  }
  
  const result = await response.json();
  console.log('Image upload response:', result);
  return result;
};

// Fetch speaker profile
export const fetchSpeakerProfile = async (): Promise<any> => {
  // Create headers for authenticated request
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };
  
  // Add the authorization token if available
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}/api/speaker/profile`, {
    method: 'GET',
    credentials: 'include',
    headers
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch speaker profile');
  }
  
  return response.json();
};

export const fetchSpeakers = async (): Promise<SpeakersResponse> => {
  const response = await fetch(`${PUBLIC_API}/speakers?limit=1000`);
  if (!response.ok) {
    throw new Error('Failed to fetch speakers');
  }
  return response.json();
};

export const fetchSessions = async (): Promise<SessionsResponse> => {
  const response = await fetch(`${PUBLIC_API}/sessions`);
  if (!response.ok) {
    throw new Error('Failed to fetch sessions');
  }
  return response.json();
};

export const fetchSponsors = async (): Promise<SponsorsResponse> => {
  const response = await fetch(`${PUBLIC_API}/sponsors?limit=1000`);
  if (!response.ok) {
    throw new Error('Failed to fetch sponsors');
  }
  return response.json();
};

export const fetchSchedule = async () => {
  const response = await fetch(`${PUBLIC_API}/schedule`);
  if (!response.ok) {
    throw new Error('Failed to fetch schedule');
  }
  return response.json();
};

export const fetchNews = async () => {
  const response = await fetch(`${PUBLIC_API}/news`);
  if (!response.ok) {
    throw new Error('Failed to fetch news');
  }
  return response.json();
};

export const fetchPresentationsList = async (): Promise<Presentation[]> => {
  const response = await fetch(`${PRESENTATIONS_API_BASE_URL}/api/presentations/published`);
  if (!response.ok) {
    throw new Error('Failed to fetch presentations');
  }
  return response.json();
};
